// Simple module-level store for contract page diagnostics
// No reactive framework — DebugDrawer reads this on render cycle

export type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

export interface ContractDiagnostic {
  orderId?: string;
  contractMode?: 'preview' | 'print' | 'ipad_sign' | 'snapshot';
  snapshotExists?: boolean;
  signatureExists?: boolean;
  templateVersion?: string;
  templateId?: string;
  identityGateOpen?: boolean;
  signDate?: string;
  signTime?: string;
  autoFilledFields?: string[];
  missingRequiredFields?: string[];
  feeSource?: string;
  partySource?: string;

  // ── Governance: customer identity / guarantor / verification ──
  /** 客戶類型：individual / company */
  customerType?: 'individual' | 'company' | null;
  /** 身分證字號資料來源：customer.identity_number / identity_document.ocr_extracted / missing */
  identityFieldSource?: string;
  /** 證件驗證狀態：verified / pending / missing / gateway_error */
  verificationStatus?: string;
  /** OCR 擷取值與主檔是否不一致 */
  ocrMismatchDetected?: boolean;
  /** 保證人資料來源：guarantor_table / missing / staging_placeholder */
  guarantorSource?: string;
  /** 保證人是否存在 */
  guarantorExists?: boolean;
  /** 當前處於可編輯狀態的欄位 key 列表 */
  editableFields?: string[];

  // ── Normalized mapping diagnostics ──
  /** 統一化後的取件日（ISO string）*/
  normalizedRentalStart?: string | null;
  /** 統一化後的還件日（ISO string）*/
  normalizedRentalEnd?: string | null;
  /** 統一化後的租金金額 */
  normalizedRentAmount?: number | null;
  /** 統一化後的押金金額 */
  normalizedDepositAmount?: number | null;
  /** 統一化後的本票金額 */
  normalizedPromissoryAmount?: number;
  /** 地址資料來源（customer.registered_address / customer.address / editable_field_override / missing）*/
  customerAddressSource?: string;
  /** 合約文字 mapping 狀態（partial / full / preview_only）*/
  contractTextMappingStatus?: 'partial' | 'full' | 'preview_only';

  // ── Snapshot diagnostics (Phase 1B) ──
  /** 是否已嘗試讀取 snapshot */
  snapshotReadAttempted?: boolean;
  /** snapshot 讀取狀態：active / archived / missing / error */
  snapshotStatus?: 'active' | 'archived' | 'missing' | 'error';
  /** snapshot 讀取錯誤碼 */
  snapshotErrorCode?: string;
  /** 功能閘道狀態 */
  snapshotFeatureGate?: 'read_only_staging';
  /** 目前已正規化並有值的 customer 欄位清單 */
  normalizedCustomerKeys?: string[];
  /** 身分證字號最終來源（customer.identity_number / editable_field_override / missing） */
  normalizedIdentitySource?: string;
  /** fullscreen dedicated signing route 是否 ready */
  fullscreenSigningReady?: boolean;

  /** create snapshot 是否啟用 */
  createSnapshotEnabled?: boolean;

  // ── Ver1103/1104 Company Settings diagnostics ──
  /** company settings source: db_table / missing */
  companySource?: 'db_table' | 'missing';
  /** whether company settings record was loaded */
  companySettingsLoaded?: boolean;
  /** whether all required company fields are present */
  companyRequiredFieldsComplete?: boolean;
  /** list of missing required company fields */
  companyMissingFields?: string[];
  /** Ver1104 — whether the edit modal/form is currently open */
  companyEditMode?: boolean;
  /** Ver1104 — last save status: idle / saving / success / error */
  companySaveStatus?: SaveStatus;

  // ── Ver1101 /admin/contracts Source-of-Truth diagnostics ──
  /** template source: db | local_seed_fallback */
  templateSource?: 'db' | 'local_seed_fallback';
  /** token schema version: 2.7 | legacy | none */
  tokenSchemaVersion?: string;
  /** template validation status: ok | has_missing | has_unknown | error */
  templateValidationStatus?: string;
  /** missing required tokens in template body (not just sample values) */
  missingRequiredTokens?: string[];
  /** whether DB active template is missing and local seed is used */
  usingLocalSeedFallback?: boolean;
  /** official snapshot blocked due to staging fallback or missing template */
  officialSnapshotBlocked?: boolean;

  // ── Token Renderer diagnostics (Ver1097) ──
  /** Token renderer 是否啟用 */
  tokenRendererEnabled?: boolean;
  /** 當前模板版本：v2.7-staging / v2.6-legacy / unknown */
  templateVersion?: string;
  /** Token render 狀態：ok / has_missing / all_missing / has_unknown / not_run */
  tokenRenderStatus?: string;
  /** 渲染時缺失的 token 列表 */
  missingTokens?: string[];
  /** 渲染時遇到的不明 namespace token 列表 */
  unknownTokens?: string[];
  /** 是否啟用 legacy compatibility mapper（v2.6 fallback） */
  legacyCompatibilityMode?: boolean;
  /** 成功替換的 token 數量 */
  tokenReplacedCount?: number;
  /** 當前是否使用 staging v2.7 mock template */
  usingStagingTemplate?: boolean;

  timestamp?: string;
}

let currentContractDiagnostic: ContractDiagnostic | null = null;

export function setContractDiagnostic(data: ContractDiagnostic | null) {
  currentContractDiagnostic = data ? { ...data, timestamp: new Date().toISOString() } : null;
}

export function getContractDiagnostic(): ContractDiagnostic | null {
  return currentContractDiagnostic;
}

export function clearContractDiagnostic(): void {
  currentContractDiagnostic = null;
}